// ── useEffectiveOwner — V50 매니저 매장 전환 훅 ──────────────────────
//
// 동작:
//   1. 본인 매장 (ownerUid === user.uid) — 기본값
//   2. 매니저로 들어간 매장 (user.managedOwnerUids 중 선택)
//
// localStorage('semfit_active_store_owner_uid')에 마지막 선택 저장
// 매니저 모드일 때 effectivePermissions를 반환해서 컴포넌트가 가드 가능

import { useEffect, useState, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../db';
import { normalizePermissions, DEFAULT_PERMISSIONS } from '../utils/permissions';

const LS_KEY = 'semfit_active_store_owner_uid';

export function useEffectiveOwner(user) {
  const [managedStores, setManagedStores] = useState([]);  // [{ ownerUid, ownerEmail, permissions }]
  const [activeOwnerUid, setActiveOwnerUid] = useState(null);
  const [loading, setLoading] = useState(true);

  // 매니저로 들어간 매장 목록 로드
  const loadStores = useCallback(async () => {
    if (!user?.uid) { setLoading(false); return; }
    setLoading(true);
    try {
      const userSnap = await getDoc(doc(db, 'users', user.uid));
      const managedOwnerUids = (userSnap.exists() && Array.isArray(userSnap.data().managedOwnerUids))
        ? userSnap.data().managedOwnerUids : [];

      const stores = [];
      for (const ownerUid of managedOwnerUids) {
        try {
          // owner의 user doc (이메일·매장명 표시용)
          let ownerEmail = '';
          let ownerStoreName = '';
          try {
            const oSnap = await getDoc(doc(db, 'users', ownerUid));
            if (oSnap.exists()) {
              ownerEmail = oSnap.data().email || '';
              ownerStoreName = oSnap.data().storeName || '';
            }
          } catch (e) { /* 권한 없으면 ok */ }

          // 본인의 매니저 doc — 권한 정보 + V95 isActive 체크
          const mSnap = await getDoc(doc(db, 'users', ownerUid, 'managers', user.uid));
          if (!mSnap.exists()) continue;  // 해제됐으면 skip

          const mData = mSnap.data();
          // V95: 사장 승인 전(isActive: false)이면 매장 셀렉터에 노출 안 함
          if (mData.isActive === false) continue;

          const permissions = normalizePermissions(mData.permissions || DEFAULT_PERMISSIONS);
          stores.push({ ownerUid, ownerEmail, ownerStoreName, permissions });
        } catch (e) { console.warn('store load failed for', ownerUid, e.message); }
      }
      setManagedStores(stores);

      // 저장된 active store 복원
      const stored = localStorage.getItem(LS_KEY);
      if (stored && (stored === user.uid || stores.some(s => s.ownerUid === stored))) {
        setActiveOwnerUid(stored);
      } else {
        setActiveOwnerUid(user.uid);  // 기본은 본인 매장
      }
    } catch (e) {
      console.warn('managed stores load failed:', e.message);
      setActiveOwnerUid(user.uid);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => { loadStores(); }, [loadStores]);

  const switchStore = useCallback((ownerUid) => {
    setActiveOwnerUid(ownerUid);
    try { localStorage.setItem(LS_KEY, ownerUid); } catch (e) {}
  }, []);

  // 현재 활성 매장 정보
  const effectiveOwnerUid = activeOwnerUid || user?.uid || null;
  const isManagerMode = effectiveOwnerUid && user?.uid && effectiveOwnerUid !== user.uid;
  const currentStore = isManagerMode
    ? (managedStores.find(s => s.ownerUid === effectiveOwnerUid) || null)
    : null;
  const effectivePermissions = isManagerMode
    ? (currentStore?.permissions || null)
    : null;  // owner 모드면 권한 무제한

  return {
    effectiveOwnerUid,
    isManagerMode,
    currentStore,            // null이면 owner 본인 매장
    effectivePermissions,    // null이면 무제한 (owner)
    managedStores,           // [{ ownerUid, ownerEmail, permissions }]
    switchStore,
    reloadStores: loadStores,
    loading,
  };
}

// 권한 체크 헬퍼 — owner 모드면 항상 true
export function makeEffectivePermissionChecker(effectivePermissions) {
  return (need, cat) => {
    // owner 모드 (permissions가 null) — 무제한
    if (!effectivePermissions) return true;
    const cur = effectivePermissions[cat] || 'none';
    if (need === 'read')  return cur === 'read' || cur === 'write';
    if (need === 'write') return cur === 'write';
    if (need === 'none')  return true;
    return false;
  };
}
